import { useEffect, useRef, useState, useCallback } from 'react';
import { unlockAudio, playPop, playChime, playFanfare, startPartyMusic, stopPartyMusic } from '../utils/sounds';

const COMPLIMENTS = [
  '🌟 Sen harikasın!',
  '🚀 Bugün bir problem çözeceksin!',
  '🎯 Odaklan, başarırsın!',
  '💡 Yeni fikirler geliyor…',
  '🏆 Diplomaya az kaldı!',
  '🎉 Tebrikler, sayfayı buldun bile!',
  '🦸 Süper kahraman modu aktif!',
  '✨ Sen code\'a değer katıyorsun!',
  '🍪 Bir kurabiye hak ettin!',
  '🎈 Tebrikler, gizli mesajı gördün!',
  '🌈 Bugün senin günün!',
  '👑 Kral/Kraliçe!',
];

const KONAMI = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
  'b', 'a',
];

const FUN_KEYWORDS: Record<string, string> = {
  party: 'PARTY MODE AKTİF! 🎉',
  rainbow: '🌈 GÖKKUŞAĞI MODU!',
  snake: '🐍 YILAN OYUNU!',
};

type Burst = { id: number; x: number; y: number; emoji: string };

const BURST_EMOJIS = ['⭐', '✨', '💫', '🌟', '🎉', '🎊', '💖', '🚀', '🎈', '🦄'];

export default function EasterEggs({ partyMode }: { partyMode: boolean }) {
  const cursorRef = useRef<HTMLDivElement>(null);
  const mousePos = useRef({ x: -100, y: -100 });
  const cursorPos = useRef({ x: -100, y: -100 });
  const rafRef = useRef<number>(0);

  // First interaction unlocks audio + triggers party fanfare + music
  const prevPartyRef = useRef(false);
  useEffect(() => {
    if (partyMode && !prevPartyRef.current) {
      unlockAudio();
      playFanfare();
      startPartyMusic();
    } else if (!partyMode && prevPartyRef.current) {
      stopPartyMusic();
    }
    prevPartyRef.current = partyMode;
  }, [partyMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPartyMusic();
  }, []);

  // Unlock audio on first user click anywhere
  useEffect(() => {
    const onFirst = () => {
      unlockAudio();
      window.removeEventListener('click', onFirst);
      window.removeEventListener('keydown', onFirst);
    };
    window.addEventListener('click', onFirst, { once: true });
    window.addEventListener('keydown', onFirst, { once: true });
    return () => {
      window.removeEventListener('click', onFirst);
      window.removeEventListener('keydown', onFirst);
    };
  }, []);

  const [bursts, setBursts] = useState<Burst[]>([]);
  const [compliment, setCompliment] = useState<string | null>(null);
  const [funMessage, setFunMessage] = useState<string | null>(null);
  const burstIdRef = useRef(0);

  // ---- Cursor follower (smooth lerp) ----
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mousePos.current.x = e.clientX;
      mousePos.current.y = e.clientY;
    };
    const onLeave = () => {
      mousePos.current.x = -100;
      mousePos.current.y = -100;
    };
    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);

    const tick = () => {
      const ease = 0.18;
      cursorPos.current.x += (mousePos.current.x - cursorPos.current.x) * ease;
      cursorPos.current.y += (mousePos.current.y - cursorPos.current.y) * ease;
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${cursorPos.current.x}px, ${cursorPos.current.y}px) translate(-50%, -50%)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ---- Click burst ----
  const addBurst = useCallback((x: number, y: number) => {
    const count = 6 + Math.floor(Math.random() * 5);
    const newBursts: Burst[] = [];
    for (let i = 0; i < count; i++) {
      burstIdRef.current += 1;
      newBursts.push({
        id: burstIdRef.current,
        x,
        y,
        emoji: BURST_EMOJIS[Math.floor(Math.random() * BURST_EMOJIS.length)],
      });
    }
    setBursts((prev) => [...prev, ...newBursts]);
    playPop();
    window.setTimeout(() => {
      setBursts((prev) => prev.filter((b) => !newBursts.find((nb) => nb.id === b.id)));
    }, 1200);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      // Don't burst on UI controls
      const target = e.target as HTMLElement | null;
      if (target?.closest('a, button, input, textarea, select, summary, details')) return;
      addBurst(e.clientX, e.clientY);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [addBurst]);

  // ---- Konami code + keyword detection ----
  useEffect(() => {
    const konamiBuffer: string[] = [];
    let typedBuffer = '';
    const onKey = (e: KeyboardEvent) => {
      konamiBuffer.push(e.key);
      if (konamiBuffer.length > KONAMI.length) konamiBuffer.shift();
      if (konamiBuffer.join(',') === KONAMI.join(',')) {
        setFunMessage('🎮 KONAMI KODU! Gizli güçler aktif!');
        playChime();
        window.setTimeout(() => setFunMessage(null), 4000);
      }

      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        typedBuffer = (typedBuffer + e.key.toLowerCase()).slice(-10);
        for (const kw of Object.keys(FUN_KEYWORDS)) {
          if (typedBuffer.endsWith(kw)) {
            setFunMessage(FUN_KEYWORDS[kw]);
            playChime();
            window.setTimeout(() => setFunMessage(null), 4000);
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---- Random compliments ----
  useEffect(() => {
    let timeoutId: number | null = null;
    const scheduleNext = () => {
      const delay = 25000 + Math.random() * 20000;
      timeoutId = window.setTimeout(() => {
        const text = COMPLIMENTS[Math.floor(Math.random() * COMPLIMENTS.length)];
        setCompliment(text);
        window.setTimeout(() => setCompliment(null), 3500);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      {/* Cursor follower */}
      <div
        ref={cursorRef}
        className={`cursor-follower ${partyMode ? 'cursor-follower--party' : ''}`}
        aria-hidden="true"
      >
        {partyMode ? '🎉' : '🎓'}
      </div>

      {/* Click bursts */}
      <div className="burst-layer" aria-hidden="true">
        {bursts.map((b) => (
          <span
            key={b.id}
            className="burst-piece"
            style={{ left: `${b.x}px`, top: `${b.y}px` }}
          >
            {b.emoji}
          </span>
        ))}
      </div>

      {/* Random compliment */}
      {compliment && (
        <div className="compliment-pop" role="status" aria-live="polite">
          {compliment}
        </div>
      )}

      {/* Konami / keyword fun message */}
      {funMessage && (
        <div className="fun-message" role="alert">
          <span className="fun-message__sparkle">✨</span>
          <span>{funMessage}</span>
          <span className="fun-message__sparkle">✨</span>
        </div>
      )}

      {/* Party mode overlay */}
      {partyMode && <PartyOverlay />}
    </>
  );
}

function PartyOverlay() {
  const orbitEmojis = ['🎈', '🎉', '🎊', '🎆', '🎇', '✨', '⭐', '💖', '🚀', '🦄', '🎵', '🎶', '🎤', '🎸', '🎺'];
  const rainEmojis = ['🎉', '🎊', '🎈', '✨', '⭐', '🎵', '🎶', '💖', '🦄', '🎀'];

  return (
    <div className="party-overlay" aria-hidden="true">
      {/* Background rainbow wash */}
      <div className="party-bg" />

      {/* Stage spotlight */}
      <div className="party-spotlight" />

      {/* Confetti rain from top */}
      <div className="party-rain">
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={`rain-${i}`}
            className="party-rain-piece"
            style={{
              left: `${(i * 3.4) % 100}%`,
              animationDelay: `${(i * 0.15) % 3}s`,
              animationDuration: `${3 + (i % 4)}s`,
              color: ['#ec4899', '#f59e0b', '#16a34a', '#0891b2', '#6366f1', '#a855f7', '#fbbf24'][i % 7],
            }}
          >
            {rainEmojis[i % rainEmojis.length]}
          </span>
        ))}
      </div>

      {/* Center title */}
      <div className="party-stage">
        <div className="party-pre">🎉 PARTİ BAŞLASIN! 🎉</div>
        <h1 className="party-title">
          {'3VIP'.split('').map((ch, i) => (
            <span
              key={i}
              className="party-letter"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </h1>
        <div className="party-sub">🎵 Herkes dansa! 🕺💃 🎵</div>
      </div>

      {/* Orbiting emojis around title */}
      <div className="party-orbit">
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (360 / 12) * i;
          return (
            <span
              key={`orbit-${i}`}
              className="party-orbit-piece"
              style={{
                ['--angle' as string]: `${angle}deg`,
                animationDelay: `${i * 0.2}s`,
              }}
            >
              {orbitEmojis[i % orbitEmojis.length]}
            </span>
          );
        })}
      </div>

      {/* DJ emojis dancing in corners */}
      <div className="party-dj party-dj--left">🕺</div>
      <div className="party-dj party-dj--right">💃</div>
      <div className="party-dj party-dj--tl">🎤</div>
      <div className="party-dj party-dj--tr">🎸</div>
      <div className="party-dj party-dj--bl">🎺</div>
      <div className="party-dj party-dj--br">🎧</div>
    </div>
  );
}